// Canvas renderer for the KG visualization. Ported from the metaphysics
// frontend so visuals (heat overlay, glow, focus highlighting) match.

import type { ForceSim2D, IGraphEdge, IGraphNode } from './force-sim';

export class GraphRenderer2D {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private sim: ForceSim2D;
  private edges: IGraphEdge[];
  private index: Map<string, IGraphNode>;
  private running = false;
  private raf = 0;
  private dpr = 1;

  private dragNode: IGraphNode | null = null;
  private dragOffX = 0;
  private dragOffY = 0;
  private panX = 0;
  private panY = 0;
  private panStartX = 0;
  private panStartY = 0;
  private isPanning = false;
  private pinchDist = 0;
  scale = 1;
  private pointerDown = false;
  private pointerDownTime = 0;
  private pointerDownPos = { x: 0, y: 0 };

  selectedId: string | null = null;
  highlightId: string | null = null;
  participatingIds: Set<string> = new Set();

  // Heat-map overlay (decayed query-participation scores).
  heatEnabled = false;
  heatScores: Map<string, number> = new Map();
  edgeHeatScores: Map<string, number> = new Map();
  /** 0 < gamma <= 1; smaller = mid-heat values get more visual contrast. */
  heatGamma = 0.5;

  onSelect: (id: string | null) => void = () => {};
  onHover: (id: string | null) => void = () => {};

  constructor(canvas: HTMLCanvasElement, sim: ForceSim2D, edges: IGraphEdge[]) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('GraphRenderer2D: 2d context unavailable');
    this.ctx = ctx;
    this.sim = sim;
    this.edges = edges;
    this.index = new Map(sim.nodes.map((n) => [n.id, n]));
    this.installPointers();
  }

  private get W() {
    return this.canvas.width / this.dpr;
  }
  private get H() {
    return this.canvas.height / this.dpr;
  }
  private get cx() {
    return this.W / 2;
  }
  private get cy() {
    return this.H / 2;
  }

  resize(w: number, h: number) {
    this.dpr = window.devicePixelRatio || 1;
    this.canvas.width = w * this.dpr;
    this.canvas.height = h * this.dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(this.dpr, this.dpr);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.loop();
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private loop = () => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.loop);
    for (let i = 0; i < 2; i++) this.sim.step(this.cx, this.cy);
    this.draw();
  };

  private fromCanvas(px: number, py: number): [number, number] {
    return [
      (px - this.panX - this.cx) / this.scale + this.cx,
      (py - this.panY - this.cy) / this.scale + this.cy,
    ];
  }

  private draw() {
    const ctx = this.ctx;
    const W = this.W;
    const H = this.H;
    ctx.clearRect(0, 0, W, H);

    const bg = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.max(W, H) * 0.75);
    bg.addColorStop(0, '#1a2428');
    bg.addColorStop(1, '#0a0f11');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    ctx.save();
    ctx.translate(this.cx, this.cy);
    ctx.scale(this.scale, this.scale);
    ctx.translate(-this.cx + this.panX / this.scale, -this.cy + this.panY / this.scale);

    this.drawEdges(ctx);
    this.drawNodes(ctx);

    ctx.restore();
  }

  private maxScore(scores: Map<string, number>): number {
    let max = 0;
    for (const v of scores.values()) if (v > max) max = v;
    return max;
  }

  private heatT(score: number, max: number): number {
    if (max <= 0 || score <= 0) return 0;
    return Math.pow(score / max, this.heatGamma);
  }

  /** Blend a hex color toward neutral graph-bg gray to drop saturation.
   *  Used to push non-retrieved nodes into the background when a query
   *  filter is active. */
  private desaturate(hex: string): string {
    const h = hex.replace('#', '');
    if (h.length !== 6) return hex;
    const n = parseInt(h, 16);
    if (Number.isNaN(n)) return hex;
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    // Full desaturation + darken: collapse to perceived luma, then bias
    // toward the graph background so non-retrieved nodes recede.
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const target = Math.round(luma * 0.45); // darken to ~45% of original luma
    const hex2 = target.toString(16).padStart(2, '0');
    return `#${hex2}${hex2}${hex2}`;
  }

  private heatStroke(t: number): string {
    const r = Math.round(80 + 175 * t);
    const g = Math.round(60 + 165 * t);
    const b = Math.round(50 + 80 * t * t);
    const a = 0.18 + 0.7 * t;
    return `rgba(${r},${g},${b},${a})`;
  }

  private drawEdges(ctx: CanvasRenderingContext2D) {
    const focus = this.selectedId || this.highlightId;
    let connected: Set<string> | null = null;
    if (focus) {
      connected = new Set([focus]);
      for (const e of this.edges) {
        if (e.source === focus) connected.add(e.target);
        if (e.target === focus) connected.add(e.source);
      }
    }

    const heatMax = this.heatEnabled ? this.maxScore(this.edgeHeatScores) : 0;

    for (const e of this.edges) {
      const s = this.index.get(e.source);
      const t = this.index.get(e.target);
      if (!s || !t) continue;
      const isHot = !!(focus && (e.source === focus || e.target === focus));
      const isDim = !!(connected && (!connected.has(e.source) || !connected.has(e.target)));
      const isParticipating =
        this.participatingIds.size > 0 &&
        this.participatingIds.has(e.source) &&
        this.participatingIds.has(e.target);

      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(t.x, t.y);

      if (this.heatEnabled && heatMax > 0) {
        const score =
          this.edgeHeatScores.get(`${e.source}|${e.target}`) ??
          this.edgeHeatScores.get(`${e.target}|${e.source}`) ??
          0;
        const heatT = this.heatT(score, heatMax);
        if (heatT > 0) {
          ctx.strokeStyle = this.heatStroke(heatT);
          ctx.lineWidth = 0.8 + 1.6 * heatT;
        } else {
          ctx.strokeStyle = 'rgba(106,122,114,0.18)';
          ctx.lineWidth = 0.5;
        }
      } else if (isParticipating) {
        ctx.strokeStyle = 'rgba(255,215,64,0.55)';
        ctx.lineWidth = 1.2;
      } else if (isHot) {
        ctx.strokeStyle = 'rgba(172,195,179,0.7)';
        ctx.lineWidth = 1.2;
      } else if (isDim) {
        ctx.strokeStyle = 'rgba(42,51,47,0.6)';
        ctx.lineWidth = 0.6;
      } else {
        ctx.strokeStyle = 'rgba(106,122,114,0.35)';
        ctx.lineWidth = 0.8;
      }
      ctx.stroke();
    }
  }

  private drawNodes(ctx: CanvasRenderingContext2D) {
    const focus = this.selectedId || this.highlightId;
    let connected: Set<string> | null = null;
    if (focus) {
      connected = new Set([focus]);
      for (const e of this.edges) {
        if (e.source === focus) connected.add(e.target);
        if (e.target === focus) connected.add(e.source);
      }
    }

    const heatMax = this.heatEnabled ? this.maxScore(this.heatScores) : 0;
    const hasQueryFilter = this.participatingIds.size > 0;

    for (const node of this.sim.nodes) {
      const isFocus = node.id === focus;
      const isConnected = connected ? connected.has(node.id) : true;
      const dim = !!(focus && !isConnected);
      const isParticipating = this.participatingIds.has(node.id);
      // Desaturate nodes outside the retrieved set when a query filter is
      // active, so "retrieved" nodes pop visually against the rest of the KG.
      const dimByQuery = hasQueryFilter && !isParticipating && !isFocus;
      const heatT =
        this.heatEnabled && heatMax > 0
          ? this.heatT(this.heatScores.get(node.id) ?? 0, heatMax)
          : 0;
      const r = node.radius * (isFocus ? 1.35 : 1 + (this.heatEnabled ? 0.5 * heatT : 0));
      const alpha = dim ? 0.2 : dimByQuery ? 0.25 : 1;
      const baseColor = !this.heatEnabled && isParticipating ? '#ffd740' : node.color;
      const baseGlow = !this.heatEnabled && isParticipating ? '#ffd740' : node.glowColor;
      const color = dimByQuery ? this.desaturate(baseColor) : baseColor;
      const glowColor = dimByQuery ? this.desaturate(baseGlow) : baseGlow;

      if (this.heatEnabled && heatT > 0 && !dim) {
        const glowR = r * (2 + 1.4 * heatT);
        const grad = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowR);
        const hotR = Math.round(80 + 175 * heatT);
        const hotG = Math.round(60 + 165 * heatT);
        const hotB = Math.round(50 + 80 * heatT * heatT);
        const rgb = `${hotR},${hotG},${hotB}`;
        grad.addColorStop(0, `rgba(${rgb},${0.25 + 0.5 * heatT})`);
        grad.addColorStop(0.5, `rgba(${rgb},${0.08 + 0.18 * heatT})`);
        grad.addColorStop(1, `rgba(${rgb},0)`);
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowR, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      if (!this.heatEnabled && !dim && !dimByQuery && (isFocus || isParticipating || !connected)) {
        const glowR = r * (isFocus ? 2.8 : isParticipating ? 2.4 : 2);
        const grad = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowR);
        const n = parseInt(glowColor.replace('#', ''), 16);
        const rgb = `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
        grad.addColorStop(0, `rgba(${rgb},${isFocus ? 0.55 : isParticipating ? 0.45 : 0.3})`);
        grad.addColorStop(0.5, `rgba(${rgb},${isFocus ? 0.18 : 0.1})`);
        grad.addColorStop(1, `rgba(${rgb},0)`);
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowR, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      if (isFocus || isParticipating || (this.heatEnabled && heatT > 0.4)) {
        ctx.strokeStyle =
          this.heatEnabled && heatT > 0
            ? `rgba(255,${Math.round(180 + 60 * heatT)},120,${0.4 + 0.5 * heatT})`
            : '#fff';
        ctx.lineWidth = (this.heatEnabled ? 0.9 + 1.6 * heatT : 1.2) / this.scale;
        ctx.stroke();
      }

      if (
        isFocus ||
        isParticipating ||
        node.id === this.highlightId ||
        (this.heatEnabled && heatT > 0.55)
      ) {
        const fs = Math.max(9, 11 / this.scale);
        ctx.font = `${fs}px "JetBrains Mono", monospace`;
        ctx.textAlign = 'center';
        ctx.fillStyle = '#f2f3f4';
        ctx.shadowColor = '#0a0f11';
        ctx.shadowBlur = 4;
        const maxLen = 24;
        const lbl = node.label.length > maxLen ? `${node.label.slice(0, maxLen - 1)}…` : node.label;
        ctx.fillText(lbl, node.x, node.y + r + fs + 2);
        ctx.shadowBlur = 0;
      }

      ctx.globalAlpha = 1;
    }
  }

  private installPointers() {
    const canvas = this.canvas;

    canvas.addEventListener('pointerdown', (e) => {
      canvas.setPointerCapture(e.pointerId);
      this.pointerDown = true;
      this.pointerDownTime = performance.now();
      this.pointerDownPos = { x: e.offsetX, y: e.offsetY };

      const [wx, wy] = this.fromCanvas(e.offsetX, e.offsetY);
      const hit = this.sim.nodeAt(wx, wy);
      if (hit) {
        this.dragNode = hit;
        this.dragOffX = hit.x - wx;
        this.dragOffY = hit.y - wy;
      } else {
        this.isPanning = true;
        this.panStartX = e.offsetX - this.panX;
        this.panStartY = e.offsetY - this.panY;
      }
    });

    canvas.addEventListener('pointermove', (e) => {
      if (!this.pointerDown) {
        const [wx, wy] = this.fromCanvas(e.offsetX, e.offsetY);
        const hit = this.sim.nodeAt(wx, wy);
        const id = hit?.id ?? null;
        if (id !== this.highlightId) {
          this.highlightId = id;
          this.onHover(id);
          canvas.style.cursor = id ? 'pointer' : 'default';
        }
        return;
      }
      if (this.dragNode) {
        const [wx, wy] = this.fromCanvas(e.offsetX, e.offsetY);
        this.dragNode.x = wx + this.dragOffX;
        this.dragNode.y = wy + this.dragOffY;
        this.dragNode.vx = 0;
        this.dragNode.vy = 0;
      } else if (this.isPanning) {
        this.panX = e.offsetX - this.panStartX;
        this.panY = e.offsetY - this.panStartY;
      }
    });

    canvas.addEventListener('pointerup', (e) => {
      this.pointerDown = false;
      const dx = e.offsetX - this.pointerDownPos.x;
      const dy = e.offsetY - this.pointerDownPos.y;
      const dt = performance.now() - this.pointerDownTime;

      if (this.dragNode && Math.sqrt(dx * dx + dy * dy) < 4 && dt < 300) {
        this.selectedId = this.dragNode.id === this.selectedId ? null : this.dragNode.id;
        this.onSelect(this.selectedId);
      } else if (!this.dragNode && Math.sqrt(dx * dx + dy * dy) < 4) {
        this.selectedId = null;
        this.onSelect(null);
      }

      this.dragNode = null;
      this.isPanning = false;
    });

    canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const factor = e.deltaY < 0 ? 1.1 : 0.91;
        const ox = e.offsetX;
        const oy = e.offsetY;
        const [wx, wy] = this.fromCanvas(ox, oy);
        this.scale = Math.max(0.15, Math.min(6, this.scale * factor));
        const newX = (wx - this.cx) * this.scale + this.cx + this.panX;
        const newY = (wy - this.cy) * this.scale + this.cy + this.panY;
        this.panX += ox - newX;
        this.panY += oy - newY;
      },
      { passive: false },
    );

    canvas.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length === 2) {
          const t0 = e.touches[0];
          const t1 = e.touches[1];
          if (!t0 || !t1) return;
          const dx = t0.clientX - t1.clientX;
          const dy = t0.clientY - t1.clientY;
          this.pinchDist = Math.sqrt(dx * dx + dy * dy);
        }
      },
      { passive: true },
    );

    canvas.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          const t0 = e.touches[0];
          const t1 = e.touches[1];
          if (!t0 || !t1) return;
          const dx = t0.clientX - t1.clientX;
          const dy = t0.clientY - t1.clientY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (this.pinchDist > 0) {
            this.scale = Math.max(0.15, Math.min(6, this.scale * (dist / this.pinchDist)));
          }
          this.pinchDist = dist;
        }
      },
      { passive: false },
    );
  }
}
